import React, { useState, useMemo } from 'react'
import { useConfigKVs, useDeleteConfigKV } from '@/app/hooks/useConfigKV'
import { useAuthStore } from '@/app/store/authStore'
import { useQuery } from '@tanstack/react-query'
import { listSites } from '@/app/services/siteService'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Download,
  Upload,
  RefreshCw,
  FileJson,
  FileText,
  Database,
  Shield,
  Clock,
  Info
} from 'lucide-react'
import ConfigKVDialog from './ConfigKVDialog'
import ConfigKVBulkOperations from './ConfigKVBulkOperations'
import { ConfigKV } from '@/app/services/configKvService'
import type { AuthState } from '@/app/store/authStore'
import type { User } from '@/app/types/auth'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'

const selectUser = (state: AuthState): User | null => state.user

const ConfigKVManagement: React.FC = () => {
  const user = useAuthStore(selectUser)
  const organizationId = useMemo(() => user?.organization_id || user?.organization?.id, [user])
  const siteId = useMemo(() => user?.site_id || user?.site?.id, [user])

  const [searchTerm, setSearchTerm] = useState('')
  const [scopeFilter, setScopeFilter] = useState<'all' | 'organization' | 'site'>('organization')
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(siteId)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [selectedConfig, setSelectedConfig] = useState<ConfigKV | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [deleteConfig, setDeleteConfig] = useState<ConfigKV | null>(null)
  const [selectedConfigs, setSelectedConfigs] = useState<Set<string>>(new Set())
  const [showBulkOperations, setShowBulkOperations] = useState(false)

  // Fetch sites for the organization
  const { data: sitesData } = useQuery({
    queryKey: ['sites', organizationId],
    queryFn: () => listSites({ page: 1, page_size: 100 }, organizationId),
    enabled: !!organizationId
  })

  const sites = sitesData?.items || []

  const queryParams = useMemo(() => {
    const params: any = {
      page,
      page_size: pageSize,
      search: searchTerm || undefined
    }

    if (scopeFilter === 'organization') {
      params.scope = 'organization'
      params.organization_id = organizationId
    } else if (scopeFilter === 'site') {
      params.scope = 'site'
      params.organization_id = organizationId
      params.site_id = selectedSiteId
    } else {
      params.organization_id = organizationId
    }

    return params
  }, [page, pageSize, searchTerm, scopeFilter, organizationId, selectedSiteId])

  const { data, isLoading, isError, refetch } = useConfigKVs(queryParams)
  const deleteConfigMutation = useDeleteConfigKV()

  const handleEdit = (config: ConfigKV) => {
    setSelectedConfig(config)
    setIsDialogOpen(true)
  }

  const handleCreate = () => {
    setSelectedConfig(null)
    setIsDialogOpen(true)
  }

  const handleDelete = (config: ConfigKV) => {
    setDeleteConfig(config)
  }

  const confirmDelete = () => {
    if (deleteConfig) {
      deleteConfigMutation.mutate({
        key: deleteConfig.key,
        params: {
          scope: deleteConfig.scope,
          organization_id: deleteConfig.organization_id || undefined,
          site_id: deleteConfig.site_id || undefined
        }
      })
      setDeleteConfig(null)
    }
  }

  const toggleSelectConfig = (key: string) => {
    const newSelected = new Set(selectedConfigs)
    if (newSelected.has(key)) {
      newSelected.delete(key)
    } else {
      newSelected.add(key)
    }
    setSelectedConfigs(newSelected)
  }

  const toggleSelectAll = () => {
    if (selectedConfigs.size === data?.items.length) {
      setSelectedConfigs(new Set())
    } else {
      setSelectedConfigs(new Set(data?.items.map(item => item.key) || []))
    }
  }

  const getDataTypeBadgeColor = (dataType: string) => {
    switch (dataType) {
      case 'string':
        return 'bg-blue-500'
      case 'number':
        return 'bg-green-500'
      case 'boolean':
        return 'bg-purple-500'
      case 'json':
        return 'bg-orange-500'
      default:
        return 'bg-gray-500'
    }
  }

  const formatValue = (config: ConfigKV) => {
    if (config.is_encrypted) {
      return '••••••••'
    }
    if (config.data_type === 'json') {
      try {
        const parsed = JSON.parse(config.value)
        // Always return a string, even for empty objects/arrays
        return JSON.stringify(parsed, null, 2)
      } catch {
        // If parsing fails, check if it's already a stringified empty object or array
        if (config.value === '{}' || config.value === '[]' || config.value === 'null') {
          return config.value
        }
        // Otherwise return the raw value as string
        return String(config.value)
      }
    }
    // Ensure we always return a string
    return String(config.value || '')
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-8">
        <p className="text-destructive">Failed to load configuration values</p>
        <Button onClick={() => refetch()} className="mt-4">
          <RefreshCw className="mr-2 h-4 w-4" /> Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Configuration Values</h3>
        <div className="flex gap-2">
          <Button onClick={() => setShowBulkOperations(true)} variant="outline">
            <Database className="mr-2 h-4 w-4" /> Bulk Operations
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" /> Add Configuration
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by key or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={scopeFilter} onValueChange={(value: any) => setScopeFilter(value)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by scope" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scopes</SelectItem>
            <SelectItem value="organization">Organization</SelectItem>
            <SelectItem value="site">Site</SelectItem>
          </SelectContent>
        </Select>
        {scopeFilter === 'site' && (
          <Select 
            value={selectedSiteId?.toString() || ''} 
            onValueChange={(value) => setSelectedSiteId(value ? Number(value) : undefined)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select a site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id.toString()}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <input
                  type="checkbox"
                  checked={selectedConfigs.size === data?.items.length && data?.items.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Features</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.items.map((config) => (
              <TableRow key={config.key}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selectedConfigs.has(config.key)}
                    onChange={() => toggleSelectConfig(config.key)}
                    className="rounded border-gray-300"
                  />
                </TableCell>
                <TableCell className="font-medium">
                  <div>
                    <div className="font-mono text-sm">{config.key}</div>
                    {config.description && (
                      <div className="text-xs text-muted-foreground mt-1">{config.description}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="max-w-[200px] truncate font-mono text-sm">
                          {formatValue(config)}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[400px]">
                        <pre className="text-xs">{formatValue(config)}</pre>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell>
                  <Badge className={getDataTypeBadgeColor(config.data_type)}>
                    {config.data_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {config.scope}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {config.is_encrypted && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Shield className="h-4 w-4 text-green-600" />
                          </TooltipTrigger>
                          <TooltipContent>Encrypted</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {config.expires_at && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Clock className="h-4 w-4 text-orange-600" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Expires: {new Date(config.expires_at).toLocaleString()}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {config.metadata && Object.keys(config.metadata).length > 0 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-blue-600" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <pre className="text-xs">
                              {JSON.stringify(config.metadata, null, 2)}
                            </pre>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(config)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(config)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data && data.total > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data.total)} of {data.total} configurations
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page * pageSize >= data.total}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ConfigKVDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        config={selectedConfig}
        scope={scopeFilter === 'all' ? 'organization' : scopeFilter}
        organizationId={organizationId}
        siteId={scopeFilter === 'site' ? selectedSiteId : undefined}
        sites={sites}
      />

      <ConfigKVBulkOperations
        open={showBulkOperations}
        onOpenChange={setShowBulkOperations}
        scope={scopeFilter === 'all' ? 'organization' : scopeFilter}
        organizationId={organizationId}
        siteId={scopeFilter === 'site' ? selectedSiteId : undefined}
        selectedKeys={Array.from(selectedConfigs)}
      />

      <AlertDialog open={!!deleteConfig} onOpenChange={() => setDeleteConfig(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the configuration key "{deleteConfig?.key}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ConfigKVManagement